/// <amd-module name="ServiceHooks/Web/Admin/Components/SubscriptionCreationPanel/SubscriptionCreationPanel" />
import { InputDescriptor, InputMode, InputValue } from "ServiceHooks/Web/Admin/Contracts/FormInput";
import * as Resources from "ServiceHooks/Web/Admin/Resources";
import { useObservable } from "VSS/Core/Observable";
import { IButtonProps } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Dialog } from "VSSUI/Dialog";
import { Dropdown, DropdownCallout } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { CustomHeader, HeaderTitleArea, TitleSize } from "VSSUI/Header";
import { Link } from "VSSUI/Link";
import { ListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelContent, PanelFooter, PanelHeader } from "VSSUI/Panel";
import { Status, StatusSize } from "VSSUI/Status";
import { Tab, TabBar } from "VSSUI/Tabs";
import { TextField } from "VSSUI/TextField";
import { Location } from "VSSUI/Utilities/Position";
import * as React from "react";
import { DirtyChangesContext } from "../../Context/DirtyChangesContext";
import { ServiceHooksDataContext } from "../../Context/ServiceHooksDataContext";
import {
    Consumer,
    ConsumerAction,
    EventTypeDescriptor,
    Notification,
    NotificationStatus,
    Subscription,
    SubscriptionInputScope
} from "../../Contracts/ServiceHooks";
import { getNotificationStatusBundle, sanitizeLinks } from "../../Utilities";
import { Form } from "../FormInput/Form";
import { FormInputDropdown } from "../FormInput/FormInput";
import {
    ISubscriptionCreationActionContentProps,
    ISubscriptionCreationBlade,
    ISubscriptionCreationPanelProps,
    ISubscriptionCreationTriggerContentProps,
    ISubscriptionTestDialogProps,
    ISubscriptionTypeSelectionPanelContentProps
} from "./SubscriptionCreationPanel.Props";


export const SubscriptionCreationPanel: React.FunctionComponent<ISubscriptionCreationPanelProps> = props => {
    const { panelRef, onDismiss, onComplete, onQueryForInputValues, onQueryTestNotification, onTest, subscriptionPartial } = props;
    const dirtyChanges = React.useRef(false);
    const [displayDiscardChangesDialog, setDisplayDiscardChangesDialog] = useObservable(false);

    const dataContext = React.useContext(ServiceHooksDataContext);

    const onDismissWrapper = () => {
        if (dirtyChanges.current) {
            setDisplayDiscardChangesDialog(true);
        } else {
            onDismiss();
        }
    };

    const [currentBlade, setCurrentBlade] = React.useState<ISubscriptionCreationBlade>(() => {
        const baseBlade: ISubscriptionCreationBlade = {
            renderContent: subscriptionPartial => (
                <SubscriptionTypeSelectionPanelContent
                    onDismiss={onDismissWrapper}
                    setCurrentBlade={setCurrentBlade}
                    selfAsBlade={currentBlade}
                    subscriptionPartial={subscriptionPartial}
                />
            )
        };

        // If consumerId and eventType are both present, this is an edit
        if (subscriptionPartial.consumerId && subscriptionPartial.eventType) {
            const consumer = dataContext.consumersById[subscriptionPartial.consumerId];
            if (consumer.externalConfiguration) {
                return createManagedElsewhereBlade(consumer);
            } else {
                return createTriggerBlade(consumer, baseBlade);
            }
        }
        return baseBlade;
    });

    return (
        <>
            <CustomPanel ref={panelRef} onDismiss={onDismissWrapper} size={ContentSize.Large}>
                <DirtyChangesContext.Provider value={{ setHasDirtyChanges: (value: boolean) => (dirtyChanges.current = value) }}>
                    {currentBlade.renderContent(
                        subscriptionPartial,
                        setCurrentBlade,
                        onDismissWrapper,
                        onQueryForInputValues.bind(null, subscriptionPartial),
                        onComplete,
                        onTest,
                        onQueryTestNotification
                    )}
                </DirtyChangesContext.Provider>
            </CustomPanel>

            <ConditionalChildren renderChildren={displayDiscardChangesDialog}>
                <Dialog
                    titleProps={{ size: TitleSize.Large, text: Resources.DialogDiscardChangesTitle }}
                    footerButtonProps={[
                        {
                            id: "cancel",
                            text: Resources.ButtonCancelText,
                            onClick: () => setDisplayDiscardChangesDialog(false)
                        },
                        {
                            id: "confirm",
                            text: Resources.ButtonConfirmText,
                            onClick: () => onDismiss(),
                            primary: true
                        }
                    ]}
                    lightDismiss={true}
                    onDismiss={() => setDisplayDiscardChangesDialog(false)}
                >
                    {Resources.DialogDiscardChangesBody}
                </Dialog>
            </ConditionalChildren>
        </>
    );
};

const SubscriptionTypeSelectionPanelContent: React.FunctionComponent<ISubscriptionTypeSelectionPanelContentProps> = props => {
    const { onDismiss, selfAsBlade, setCurrentBlade, subscriptionPartial } = props;

    const dataContext = React.useContext(ServiceHooksDataContext);
    const dirtyChangesContext = React.useContext(DirtyChangesContext);

    const [dropdownSelection] = React.useState(() => {
        const initialSelectedIndex = subscriptionPartial.consumerId
            ? Math.max(
                  dataContext.consumers.findIndex(x => x.id === subscriptionPartial.consumerId),
                  0
              )
            : 0;
        return new ListSelection({ selectedRanges: [{ beginIndex: initialSelectedIndex, endIndex: initialSelectedIndex }], selectOnFocus: false });
    });

    const onDropdownItemSelected = (event: any, listItem: IListBoxItem<Consumer>) => {
        subscriptionPartial.consumerId = listItem.id;
        dirtyChangesContext.setHasDirtyChanges(true);
    };

    return (
        <>
            <PanelHeader
                description={Resources.BladeServiceSubtitle}
                onDismiss={onDismiss}
                titleProps={{ text: Resources.BladeServiceTitle, size: TitleSize.Large }}
            />

            <PanelContent>
                <div className="flex-column flex-grow rhythm-vertical-8">
                    <Dropdown
                        items={dataContext.consumers.map(consumer => {
                            return { data: consumer, id: consumer.id, text: consumer.name } as IListBoxItem<Consumer>;
                        })}
                        selection={dropdownSelection}
                        onSelect={onDropdownItemSelected}
                    />
                    <Observer selection={dropdownSelection}>
                        {() => {
                            if (!dropdownSelection.value[0]) {
                                return null;
                            }

                            const selectedItem = dataContext.consumers[dropdownSelection.value[0].beginIndex];

                            const eventTypeIds: string[] = Array.from(new Set(selectedItem.actions
                                .map(x => x.supportedEventTypes)
                                .reduce((prev, curr) => prev.concat(curr))));
                            
                            let eventNames: string[] = [];
                            let actionNames: string[] = selectedItem.actions
                                .map(x => x.name);
                            
                            if (eventTypeIds.findIndex(item => item === "*") > -1) {
                                eventNames = [Resources.BladeServiceAllEvents];
                            } else {
                                eventNames = eventTypeIds.filter(id => dataContext.eventTypesById[id]).map(id => dataContext.eventTypesById[id].name);
                            }

                            return (
                                <div className="service-details flex-column rhythm-vertical-8">
                                    <div className="hooks-service-details-header title-s">{Resources.BladeServiceDescription}</div>
                                    <div className="service-details-description body-m">{sanitizeLinks(selectedItem.description)}</div>
                                    <div className="hooks-service-details-header title-s">{Resources.BladeServiceSupportedEvents}</div>
                                    {eventNames.map((eventName, index) => (
                                        <div key={"supported-event-" + index} className="service-details-indent-text body-m">
                                            {eventName}
                                        </div>
                                    ))}
                                    <div className="hooks-service-details-header title-s">{Resources.BladeServiceSupportedActions}</div>
                                    {actionNames.map((actionName, index) => (
                                        <div key={"supported-action-" + index} className="service-details-indent-text body-m">
                                            {actionName}
                                        </div>
                                    ))}
                                </div>
                            );
                        }}
                    </Observer>
                </div>
            </PanelContent>

            <Observer selection={dropdownSelection}>
                {() => {
                    return (
                        <PanelFooter
                            buttonProps={[
                                { text: Resources.ButtonCancelText, onClick: onDismiss },
                                {
                                    text: Resources.ButtonNextText,
                                    disabled: dropdownSelection.value[0] === undefined,
                                    primary: true,
                                    onClick: () => {
                                        if (dataContext.consumersById[subscriptionPartial.consumerId!].externalConfiguration) {
                                            setCurrentBlade(
                                                createManagedElsewhereBlade(dataContext.consumersById[subscriptionPartial.consumerId!], selfAsBlade)
                                            );
                                        } else {
                                            setCurrentBlade(
                                                createTriggerBlade(dataContext.consumersById[subscriptionPartial.consumerId!], selfAsBlade)
                                            );
                                        }
                                    }
                                }
                            ]}
                        />
                    );
                }}
            </Observer>
        </>
    );
};

function createManagedElsewhereBlade(consumer: Consumer, parentBlade?: ISubscriptionCreationBlade): ISubscriptionCreationBlade {
    return {
        renderContent: (subscriptionPartial, setCurrentBlade, onDismiss) => (
            <>
                <PanelHeader titleProps={{ text: consumer.name, size: TitleSize.Large }} onDismiss={onDismiss} />

                <PanelContent>
                    <div className="primary-text">
                        {Resources.BladeManagedElsewhereBody}
                        <Link 
                            href={consumer.externalConfiguration.createSubscriptionUrl} 
                            target="_blank"
                            underline={true}
                        >
                            {consumer.name}
                        </Link>
                    </div>
                </PanelContent>

                <PanelFooter buttonProps={parentBlade ? [createFooterBackButton(setCurrentBlade, parentBlade)] : []} />
            </>
        )
    };
}

function createTriggerBlade(consumer: Consumer, parentBlade: ISubscriptionCreationBlade): ISubscriptionCreationBlade {
    const triggerBlade: ISubscriptionCreationBlade = {
        renderContent: (subscriptionPartial, setCurrentBlade, onDismiss, onQueryForInputValues) => (
            <SubscriptionCreationTriggerContent
                consumer={consumer}
                selfAsBlade={triggerBlade}
                setCurrentBlade={setCurrentBlade}
                subscriptionPartial={subscriptionPartial}
                onDismiss={onDismiss}
                onQueryForInputValues={onQueryForInputValues}
                parentBlade={parentBlade}
            />
        )
    };

    return triggerBlade;
}

const SubscriptionCreationTriggerContent: React.FunctionComponent<ISubscriptionCreationTriggerContentProps> = props => {
    const { consumer, setCurrentBlade, selfAsBlade, subscriptionPartial, onDismiss, onQueryForInputValues, parentBlade } = props;
    const dataContext = React.useContext(ServiceHooksDataContext);
    const dirtyChangesContext = React.useContext(DirtyChangesContext);

    const [listBoxItems] = React.useState(() => {
        let eventTypeIds: string[] = Array.from(new Set(consumer.actions
            .map(x => x.supportedEventTypes)
            .reduce((prev, curr) => prev.concat(curr))));
        
        if (eventTypeIds.findIndex(item => item === "*") > -1) {
            eventTypeIds = dataContext.eventTypes.map(eventType => eventType.id);
        }

        return eventTypeIds
            .filter(id => dataContext.eventTypesById[id])
            .map(id => {
                const item = dataContext.eventTypesById[id];
                return { data: item, text: item.name, id: id } as IListBoxItem<EventTypeDescriptor>;
            });
    });

    const [selection] = React.useState(() => {
        const initialSelectedIndex = subscriptionPartial.eventType
            ? Math.max(
                  listBoxItems.findIndex(x => x.id === subscriptionPartial.eventType),
                  0
              )
            : 0;

        return new ListSelection({
            selectedRanges: listBoxItems.length > 0 ? [{ beginIndex: initialSelectedIndex, endIndex: initialSelectedIndex }] : undefined,
            selectOnFocus: false
        });
    });

    const onSelectionChanged = () => {
        dirtyChangesContext.setHasDirtyChanges(true);
    };

    React.useEffect(() => {
        selection.subscribe(onSelectionChanged);
        return () => selection.unsubscribe(onSelectionChanged);
    }, []);

    const [isFormValid, setIsFormValid] = useObservable(false);

    return (
        <>
            <PanelHeader
                description={Resources.BladeTriggerSubtitle}
                onDismiss={onDismiss}
                titleProps={{ text: Resources.BladeTriggerTitle, size: TitleSize.Large }}
            />

            <PanelContent>
                <div className="flex-column flex-grow flex-start rhythm-vertical-8">
                    <FormItem className="flex-self-stretch" label={<div className="title-xs">{Resources.BladeTriggerEventTypeLabel}</div>}>
                        <Dropdown
                            items={listBoxItems}
                            renderCallout={props => {
                                return (
                                    <DropdownCallout
                                        {...props}
                                        anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                                        dropdownOrigin={{ horizontal: Location.start, vertical: Location.start }}
                                    />
                                );
                            }}
                            selection={selection}
                        />
                    </FormItem>
                    <div className="hooks-trigger-visibility-blurb flex-row">
                        <div className="primary-text">{Resources.BladeTriggerVisibilityBlurb}</div>
                    </div>
                    <Observer
                        selection={{
                            observableValue: selection,
                            filter: () => {
                                subscriptionPartial.publisherInputs = {};
                                subscriptionPartial.consumerInputs = {};
                                return true;
                            }
                        }}
                    >
                        {() => {
                            const selectedItem: EventTypeDescriptor | undefined = selection.value[0]
                                ? listBoxItems[selection.value[0].beginIndex].data
                                : undefined;

                            if (selectedItem) {
                                subscriptionPartial.eventType = selectedItem.id;
                                subscriptionPartial.publisherId = selectedItem.publisherId;

                                const valueUpdateFunction = (descriptor: InputDescriptor, newValue: string | undefined) => {
                                    mapChangeToDictionary(subscriptionPartial.publisherInputs!, descriptor, newValue);
                                    dirtyChangesContext.setHasDirtyChanges(true);
                                };

                                return (
                                    <>
                                        <Form
                                            key={selectedItem ? selectedItem.id : "no-selected"}
                                            inputDescriptors={selectedItem.inputDescriptors}
                                            inputValues={subscriptionPartial.publisherInputs!}
                                            onFetchInputDetails={inputValues => onQueryForInputValues(inputValues, SubscriptionInputScope.Publisher)}
                                            onValidate={setIsFormValid}
                                            onItemValueChange={valueUpdateFunction}
                                            title={Resources.BladeTriggerFilters}
                                        />
                                    </>
                                );
                            }

                            return null;
                        }}
                    </Observer>
                </div>
            </PanelContent>

            <Observer isFormValid={isFormValid}>
                {(observerProps: { isFormValid: boolean }) => (
                    <PanelFooter
                        buttonProps={[
                            createFooterBackButton(setCurrentBlade, parentBlade),
                            {
                                id: "next",
                                text: Resources.ButtonNextText,
                                disabled: !observerProps.isFormValid,
                                primary: true,
                                onClick: () => setCurrentBlade(createActionBlade(selfAsBlade))
                            }
                        ]}
                    />
                )}
            </Observer>
        </>
    );
};

function createActionBlade(parentBlade: ISubscriptionCreationBlade): ISubscriptionCreationBlade {
    const actionBlade: ISubscriptionCreationBlade = {
        renderContent: (subscriptionPartial, setCurrentBlade, onDismiss, onQueryForInputValues, onComplete, onTest, onQueryTestNotification) => (
            <SubscriptionCreationActionContent
                onComplete={onComplete}
                onDismiss={onDismiss}
                onQueryForInputValues={onQueryForInputValues}
                onQueryTestNotification={onQueryTestNotification}
                onTest={onTest}
                parentBlade={parentBlade}
                selfAsBlade={actionBlade}
                setCurrentBlade={setCurrentBlade}
                subscriptionPartial={subscriptionPartial}
            />
        )
    };

    return actionBlade;
}

const SubscriptionCreationActionContent: React.FunctionComponent<ISubscriptionCreationActionContentProps> = props => {
    const {
        onDismiss,
        onComplete,
        onQueryForInputValues,
        onQueryTestNotification,
        onTest,
        parentBlade,
        setCurrentBlade,
        subscriptionPartial
    } = props;

    const dataContext = React.useContext(ServiceHooksDataContext);
    const dirtyChangesContext = React.useContext(DirtyChangesContext);

    const consumer = dataContext.consumersById[subscriptionPartial.consumerId!];
    const actions = consumer.actions;

    const [listBoxItems] = React.useState(() =>
        actions.map(action => {
            return { data: action, text: action.name, id: action.id } as IListBoxItem<ConsumerAction>;
        })
    );

    const [selection] = React.useState(() => {
        const initialSelectedIndex = subscriptionPartial.consumerActionId
            ? Math.max(
                  listBoxItems.findIndex(x => x.id === subscriptionPartial.consumerActionId),
                  0
              )
            : 0;

        return new ListSelection({
            selectedRanges: listBoxItems.length > 0 ? [{ beginIndex: initialSelectedIndex, endIndex: initialSelectedIndex }] : undefined,
            selectOnFocus: false
        });
    });

    const [isFormValid, setIsFormValid] = useObservable(false);
    const [awaitingFinishPromise, setAwaitingFinishPromise] = useObservable(false);
    const [resourceVersion, setResourceVersion] = useObservable<string>(subscriptionPartial.resourceVersion || "1.0");
    const [errorMessage, setErrorMessage] = useObservable<string>("");

    const [displayTestDialog, setDisplayTestDialog] = useObservable(false);
    const [testNotification, setTestNotification] = useObservable<Notification | undefined>(undefined);

    const onResourceVersionChanged = (newValue: string) => {
        setResourceVersion(newValue);
        subscriptionPartial.resourceVersion = newValue;
        dirtyChangesContext.setHasDirtyChanges(true);
    };

    const onDialogOpen = () => {
        setDisplayTestDialog(true);
    }

    const onDialogDismiss = () => {
        setDisplayTestDialog(false);
    };

    const onTestWrapper = () => {
        onTest(subscriptionPartial as Subscription)
            .then(result => {
                setTestNotification(result);
                if (result.status !== NotificationStatus.Completed) {
                    const intervalId = setInterval(() => {
                        onQueryTestNotification(result.subscriptionId, result.id).then(result => {
                            setTestNotification(result);
                            if (result.status === NotificationStatus.Completed) {
                                clearInterval(intervalId);
                            }
                        });
                    }, 200);
                }
            })
            .catch(reason => {
                setErrorMessage(reason.message as string);
            });
    };

    const onSelectionChanged = () => {
        dirtyChangesContext.setHasDirtyChanges(true);
    };

    React.useEffect(() => {
        selection.subscribe(onSelectionChanged);
        return () => selection.unsubscribe(onSelectionChanged);
    });

    return (
        <>
            <PanelHeader
                description={Resources.BladeActionSubtitle}
                onDismiss={onDismiss}
                titleProps={{ text: Resources.BladeActionTitle, size: TitleSize.Large }}
            />
            <CustomHeader>
                <HeaderTitleArea>
                    <FormItem 
                        className="flex-self-stretch margin-bottom-8" 
                        label={<div className="title-xs">{Resources.BladeActionPerformedLabel}</div>}
                    >
                        <Dropdown
                            items={listBoxItems}
                            renderCallout={props => {
                                return (
                                    <DropdownCallout
                                        {...props}
                                        anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                                        dropdownOrigin={{ horizontal: Location.start, vertical: Location.start }}
                                    />
                                );
                            }}
                            selection={selection}
                        />
                    </FormItem>
                </HeaderTitleArea>
            </CustomHeader>
            <div className="hooks-panel-separator"/>
            <PanelContent>
                <div className="flex-column flex-grow flex-start rhythm-vertical-8">
                    <Observer errorMessage={errorMessage}>
                        {(observerProps: { errorMessage: string }) => {
                            return observerProps.errorMessage.length > 0 ? (
                                <MessageCard severity={MessageCardSeverity.Error}>{sanitizeLinks(observerProps.errorMessage)}</MessageCard>
                            ) : null;
                        }}
                    </Observer>
                    <Observer
                        selection={{
                            observableValue: selection,
                            filter: () => {
                                subscriptionPartial.consumerInputs = {};
                                return true;
                            }
                        }}
                    >
                        {() => {
                            const selectedAction: ConsumerAction | undefined = selection.value[0]
                                ? listBoxItems[selection.value[0].beginIndex].data
                                : undefined;

                            if (selectedAction) {
                                subscriptionPartial.consumerActionId = selectedAction.id;

                                const inputDescriptors = [...selectedAction.inputDescriptors, ...consumer.inputDescriptors];

                                const valueUpdateFunction = (descriptor: InputDescriptor, newValue: string | undefined) => {
                                    mapChangeToDictionary(subscriptionPartial.consumerInputs!, descriptor, newValue);
                                    dirtyChangesContext.setHasDirtyChanges(true);
                                };

                                const selectedEvent = dataContext.eventTypesById[subscriptionPartial.eventType!];
                                const eventSupportedVersions = selectedEvent.supportedResourceVersions;

                                let supportedResourceVersions: string[] = [];
                                if (selectedAction.supportedResourceVersions !== undefined && selectedAction.supportedResourceVersions[selectedEvent.id]) {
                                    const actionSupportedVersions = selectedAction.supportedResourceVersions[selectedEvent.id];
                                    supportedResourceVersions = eventSupportedVersions.filter(
                                        testVal => actionSupportedVersions.indexOf(testVal) !== -1
                                    );
                                } else {
                                    supportedResourceVersions = eventSupportedVersions;
                                }
                                const supportedResourceVersionsInputValues: InputValue[] = [
                                    ...supportedResourceVersions.map(x => {
                                        return { data: {}, displayValue: x, value: x };
                                    }),
                                    { data: {}, value: "latest", displayValue: Resources.ResourceVersionLatest }
                                ];

                                return (
                                    <>
                                        <Form
                                            key={selectedAction ? selectedAction.id : "no-selected"}
                                            inputDescriptors={inputDescriptors}
                                            inputValues={subscriptionPartial.consumerInputs!}
                                            onFetchInputDetails={inputValues => onQueryForInputValues(inputValues, SubscriptionInputScope.Consumer)}
                                            onValidate={setIsFormValid}
                                            onItemValueChange={valueUpdateFunction}
                                            title={Resources.BladeActionSettings}
                                        >
                                            <ConditionalChildren
                                                renderChildren={selectedAction.allowResourceVersionOverride === true && supportedResourceVersions.length > 1}
                                            >
                                                <Observer resourceVersion={resourceVersion}>
                                                    {(observerProps: { resourceVersion: string }) => {
                                                        // Some janky partial conversions happening here;
                                                        // only using fields that the forminput cares about
                                                        // keeps from having to write another component
                                                        return (
                                                            <FormInputDropdown
                                                                inputDescriptor={
                                                                    ({
                                                                        name: "Resource Version",
                                                                        values: {
                                                                            possibleValues: supportedResourceVersionsInputValues
                                                                        }
                                                                    } as Partial<InputDescriptor>) as InputDescriptor
                                                                }
                                                                onValueChange={onResourceVersionChanged}
                                                                value={observerProps.resourceVersion}
                                                                validity={{ valid: true, errors: [] }}
                                                            />
                                                        );
                                                    }}
                                                </Observer>
                                            </ConditionalChildren>
                                        </Form>
                                    </>
                                );
                            }
                            return null;
                        }}
                    </Observer>
                </div>
            </PanelContent>
            <div className="hooks-panel-separator"/>
            <CustomHeader>
                <HeaderTitleArea>
                    <div className="title-s margin-bottom-8">{Resources.BladeActionTestSubscription}</div>
                    <Observer
                        testNotification={{
                            observableValue: testNotification,
                        }}
                    >
                        {() => {
                            if (testNotification.value !== undefined) {
                                const statusBundle = getNotificationStatusBundle(testNotification.value);
                                const notificationStatus = testNotification.value.status;
                                return (
                                    <>
                                        <div className="flex-row">
                                            <Status {...statusBundle.statusProps} className="icon-margin flex-self-center" size={StatusSize.m} />
                                            <div className="body-m flex-self-center margin-right-4">{statusBundle.label}</div>
                                            {notificationStatus === NotificationStatus.Completed && (
                                                <Link underline={true} onClick={onDialogOpen}>
                                                    {Resources.BladeActionTestSubscriptionViewDetails}
                                                </Link>
                                            )}
                                        </div>
                                    </>
                                )
                            }
                            return (
                                <>
                                    {Resources.BladeActionTestSubscriptionNotTestedYet}
                                </>
                            )  
                        }}
                    </Observer>
                </HeaderTitleArea>
            </CustomHeader>
            <Observer isFormValid={isFormValid} awaitingFinishPromise={awaitingFinishPromise}>
                {(observerProps: { isFormValid: boolean; awaitingFinishPromise: boolean }) => (
                    <PanelFooter
                        buttonProps={[
                            createFooterBackButton(setCurrentBlade, parentBlade),
                            {
                                id: "test",
                                disabled: !observerProps.isFormValid && !observerProps.awaitingFinishPromise,
                                text: Resources.ButtonTestText,
                                onClick: onTestWrapper
                            },
                            {
                                id: "save",
                                disabled: !observerProps.isFormValid && !observerProps.awaitingFinishPromise,
                                text: Resources.ButtonSaveText,
                                primary: true,
                                onClick: () => {
                                    setAwaitingFinishPromise(true);
                                    onComplete(subscriptionPartial as Subscription)
                                        .then(() => setAwaitingFinishPromise(false))
                                        .catch(reason => {
                                            setErrorMessage(reason.message as string);
                                        });
                                }
                            }
                        ]}
                    />
                )}
            </Observer>

            <ConditionalChildren renderChildren={displayTestDialog}>
                <SubscriptionTestDialog onDismiss={onDialogDismiss} notification={testNotification} />
            </ConditionalChildren>
        </>
    );
};

const SubscriptionTestTextRenderer: React.FunctionComponent<{ value: string }> = props => {
    return (
        <TextField
            className="hooks-test-dialog-text-renderer flex-grow"
            containerClassName="flex-grow"
            inputClassName="flex-self-stretch"
            multiline={true}
            readOnly={true}
            resizable={false}
            value={props.value}
        />
    );
};

const SubscriptionTestDialog: React.FunctionComponent<ISubscriptionTestDialogProps> = props => {
    const { onDismiss, notification } = props;
    const dataContext = React.useContext(ServiceHooksDataContext);

    const [selectedTab, setSelectedTab] = useObservable("summary");

    const getTabContentFromId = (selectedTabId: string, notification: Notification) => {
        switch (selectedTabId) {
            case "event":
                return <SubscriptionTestTextRenderer key="event" value={JSON.stringify(notification.details.event, null, 2)} />;
            case "request":
                return <SubscriptionTestTextRenderer key="request" value={notification.details.request} />;
            case "response":
                return <SubscriptionTestTextRenderer key="response" value={notification.details.response} />;
            default:
                const statusBundle = getNotificationStatusBundle(notification);
                return (
                    <div className="test-dialog-summary-content flex-column flex-grow rhythm-vertical-16">
                        <div className="rhythm-vertical-8">
                            <div className="title-xs">{Resources.TestSubscriptionTabSummaryStatus}</div>
                            <div className="flex-row">
                                <Status {...statusBundle.statusProps} className="icon-margin flex-self-center" size={StatusSize.m} />
                                <div className="body-m flex-self-center">{statusBundle.label}</div>
                            </div>
                        </div>
                        <div>
                            <div className="body-m">{`${
                                Resources.TestSubscriptionTabSummarySentAt
                            } ${notification.createdDate.toLocaleString()}`}</div>
                        </div>
                    </div>
                );
        }
    };

    return (
        <Dialog
            calloutContentClassName={"hooks-test-subscription-dialog"}
            footerButtonProps={[
                {
                    id: "finish",
                    text: Resources.ButtonCloseText,
                    primary: true,
                    onClick: onDismiss
                }
            ]}
            onDismiss={onDismiss}
            titleProps={{ size: TitleSize.Large, text: Resources.TestSubscriptionDialogTitle }}
        >
            <Observer notification={notification}>
                {(observerProps: { notification: Notification | undefined }) => {
                    if (!observerProps.notification) {
                        return null;
                    }

                    const notification = observerProps.notification;
                    const consumerId = notification.details.consumerId;
                    const actionId = notification.details.consumerActionId;

                    const consumerName = dataContext.consumersById[consumerId] ? dataContext.consumersById[consumerId].name : Resources.Unknown;
                    const actionName = dataContext.consumerActionsById[consumerId + actionId]
                        ? dataContext.consumerActionsById[consumerId + actionId].name
                        : Resources.Unknown;

                    return (
                        <>
                            <div className="test-dialog-subtitle title-s">{`${consumerName} (${actionName})`}</div>
                            <TabBar className={"hooks-test-dialog-tab-bar"} selectedTabId={selectedTab} onSelectedTabChanged={setSelectedTab}>
                                <Tab id="summary" name={Resources.TestSubscriptionTabSummaryTitle} />
                                {notification.details.event && <Tab id="event" name={Resources.TestSubscriptionTabEventTitle} />}
                                {notification.details.request && <Tab id="request" name={Resources.TestSubscriptionTabRequestTitle} />}
                                {notification.details.response && <Tab id="response" name={Resources.TestSubscriptionTabResponseTitle} />}
                            </TabBar>
                            <Observer selectedTabId={selectedTab}>
                                {(observerProps: { selectedTabId: string }) => getTabContentFromId(observerProps.selectedTabId, notification)}
                            </Observer>
                        </>
                    );
                }}
            </Observer>
        </Dialog>
    );
};

function createFooterBackButton(
    setCurrentBlade: (newBlade: ISubscriptionCreationBlade) => void,
    parentBlade: ISubscriptionCreationBlade
): IButtonProps {
    return {
        id: "back",
        text: Resources.ButtonBackText,
        onClick: () => {
            setCurrentBlade(parentBlade);
        }
    };
}

function mapChangeToDictionary(dictionary: { [key: string]: string }, descriptor: InputDescriptor, newValue?: string): void {
    if (descriptor.inputMode === InputMode.Combo) {
        if (newValue) {
            dictionary[descriptor.id] = newValue;
        } else {
            delete dictionary[descriptor.id];
        }
    } else if (newValue) {
        dictionary[descriptor.id] = newValue;
    } else {
        delete dictionary[descriptor.id];
    }
}
